// ----------------------------------------------------------------------------
// Snippet: Country Switcher
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

// Styles
import './_country-switcher.scss';


const OPEN_CLASS = 'is-open';
const MODAL_CLOSE_TRIGGER = '[data-close-modal="true"]';

class LocalizationForm extends HTMLElement {
    constructor() {
        super();

        this.elements = {
            countryInput: this.querySelector('input[name="country_code"]'),
            languageInput: this.querySelector('input[name="language_code"]'),
            button: this.querySelector('button'),
            panel: this.querySelector('.country-modal, ul'),
            parentNav: this.closest('.header-menu__item')
        };

        if (!this.elements.button || !this.elements.panel) {
            return;
        }

        this.isCountryModal = this.elements.button.hasAttribute('data-modal-toggle');
        this.onDocumentKeyUp = this.onDocumentKeyUp.bind(this);
        this.onPanelClick = this.onPanelClick.bind(this);

        this.elements.button.addEventListener('click', this.openSelector.bind(this));
        if (!this.isCountryModal) {
            this.elements.button.addEventListener('focusout', this.closeSelector.bind(this));
        }
        this.addEventListener('keyup', this.onContainerKeyUp.bind(this));

        if (!this.isCountryModal && this.elements.panel.lastElementChild) {
            this.elements.panel.lastElementChild.addEventListener('keydown', function(event) {
                if (event.keyCode === 9) {
                    this.parentNode.toggleAttribute('hidden');
                }
            });
        }

        if (this.isCountryModal) {
            this.elements.panel.addEventListener('click', this.onPanelClick);
        }

        this.querySelectorAll('a').forEach(item => item.addEventListener('click', this.onItemClick.bind(this)));

        // Add event listeners for individual country labels
        this.querySelectorAll('.country-modal__country-label').forEach(label => {
            label.addEventListener('click', this.onLabelClick.bind(this));
        });
    }

    hidePanel() {
        this.elements.parentNav?.classList.remove('js--is-open');
        this.elements.button.setAttribute('aria-expanded', 'false');

        if (this.isCountryModal) {
            this.elements.panel.classList.remove(OPEN_CLASS);
            document.body.classList.remove('country-modal-open');

            window.setTimeout(() => {
                this.elements.panel.setAttribute('hidden', true);
            }, 250);
            document.removeEventListener('keyup', this.onDocumentKeyUp);
            return;
        }

        this.elements.panel.setAttribute('hidden', true);
    }


    onContainerKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        this.hidePanel();
        this.elements.button.focus();
    }

    onDocumentKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;
        this.hidePanel();
        this.elements.button.focus();
    }

    onPanelClick(event) {
        if (!event.target.closest(MODAL_CLOSE_TRIGGER)) return;
        this.hidePanel();
    }

    onLabelClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const form = this.querySelector('form');
        const label = event.currentTarget;
        const languageValue = label.dataset.languageValue;

        // Find the parent country link to get the country value
        const countryLink = label.closest('.country-modal__country-link');
        const countryValue = countryLink ? countryLink.dataset.countryValue : null;

        if (countryValue && this.elements.countryInput) {
            this.elements.countryInput.value = countryValue;
        }

        if (languageValue && this.elements.languageInput) {
            this.elements.languageInput.value = languageValue;
        }

        if (form) form.submit();
    }

    onItemClick(event) {
        event.preventDefault();
        const form = this.querySelector('form');
        const countryValue = event.currentTarget.dataset.countryValue;
        const languageValue = event.currentTarget.dataset.languageValue;
        const fallbackValue = event.currentTarget.dataset.value;

        if (countryValue && this.elements.countryInput) {
            this.elements.countryInput.value = countryValue;
        }

        if (languageValue && this.elements.languageInput) {
            this.elements.languageInput.value = languageValue;
        }

        // Backward compatibility for any links still using data-value.
        if (fallbackValue && !countryValue && !languageValue) {
            if (this.elements.languageInput && !this.elements.countryInput) {
                this.elements.languageInput.value = fallbackValue;
            } else if (this.elements.countryInput) {
                this.elements.countryInput.value = fallbackValue;
            }
        }

        if (form) form.submit();
    }

    openSelector() {
        this.elements.parentNav?.classList.toggle('js--is-open');
        this.elements.button.focus();

        if (this.isCountryModal) {
            const isOpening = this.elements.button.getAttribute('aria-expanded') === 'false';

            if (isOpening) {
                this.elements.panel.removeAttribute('hidden');
                window.requestAnimationFrame(() => {
                    this.elements.panel.classList.add(OPEN_CLASS);
                });
                document.body.classList.add('country-modal-open');
                document.addEventListener('keyup', this.onDocumentKeyUp);
            } else {
                this.hidePanel();
            }

            this.elements.button.setAttribute('aria-expanded', isOpening.toString());
            return;
        }

        this.elements.panel.toggleAttribute('hidden');
        this.elements.button.setAttribute('aria-expanded', (this.elements.button.getAttribute('aria-expanded') === 'false').toString());
    }

    closeSelector(event) {
        if (this.isCountryModal) {
            return;
        }

        const shouldClose = event.relatedTarget && event.relatedTarget.nodeName === 'BUTTON';
        if (event.relatedTarget === null || shouldClose) {
            this.hidePanel();
        }
    }

}

customElements.define('localization-form', LocalizationForm);
